import React, { useCallback, useEffect, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import LoadingScreen from "../../screens/LoadingScreen";
import { DashboardShell } from "../campaign-renewals/DashboardShell";
import { DashboardHeader } from "../campaign-renewals/DashboardHeader";
import { MetricCards } from "../campaign-renewals/MetricCards";
import { RenewalList } from "../campaign-renewals/RenewalsList";
import { DashboardMetrics, PaginatedListingSyncLogs } from "./types";
import { getDashboardMetrics, getPaginatedListingSyncs } from "./actions";

const ListingSync = () => {
    const mobile = useIsMobile(900);

    const [renewalsPage, setRenewalsPage] = useState(1);
    const [limit] = useState(10);
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [error, setError] = useState(false);
    const [syncLogs, setSyncLogs] = useState<PaginatedListingSyncLogs>();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [sortBy, setSortBy] = useState<string | undefined>();
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const fetchMetrics = async () => {
        try {
            const data = await getDashboardMetrics();
            setMetrics(data);
        } catch (error) {
            console.error("Failed to fetch dashboard metrics:", error);
            setError(true);
        }
    };

    const fetchRenewals = useCallback(
        async (p: number = renewalsPage) => {
            try {
                const data = await getPaginatedListingSyncs(
                    p,
                    limit,
                    searchTerm,
                    statusFilter,
                    sortBy,
                    sortDirection
                );
                setSyncLogs(data);
            } catch (error) {
                console.error("Failed to fetch renewals:", error);
                setError(true);
            }
        },
        [limit, renewalsPage, searchTerm, statusFilter, sortBy, sortDirection]
    );

    useEffect(() => {
        fetchMetrics();
    }, []);

    useEffect(() => {
        fetchRenewals(renewalsPage);
    }, [renewalsPage, fetchRenewals]);

    const handleSortChange = (newSortBy: string, newSortDirection: "asc" | "desc") => {
        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
        setRenewalsPage(1);
    };

    if (error) return <div>Error loading metrics</div>;
    if (!metrics) return <LoadingScreen />;

    return (
        <div className={mobile ? "p-4" : "p-6"}>
            <DashboardShell>
                <DashboardHeader
                    heading="MLS Listing Sync Dashboard"
                    text="Track listing syncs"
                />

                {/* Parent grid: single column on mobile, 6 columns on large */}
                <div
                    className={
                        mobile
                            ? "grid gap-6 grid-cols-1"
                            : "grid gap-6 grid-cols-1 lg:grid-cols-6"
                    }
                >
                    {/* Metric cards: full width, horizontally scrollable on mobile */}
                    <div className="col-span-full">
                        <div className={mobile ? "overflow-x-auto" : ""}>
                            <MetricCards
                                metrics={metrics}
                                isMobile={mobile}
                                sync={true}
                            />
                        </div>
                    </div>

                    {/* Upcoming renewals list */}
                    <RenewalList
                        renewals={syncLogs?.data || []}
                        stale={false}
                        fetchMetrics={fetchMetrics}
                        fetchRenewals={fetchRenewals}
                        page={renewalsPage}
                        setPage={setRenewalsPage}
                        limit={limit}
                        total={syncLogs?.total || 0}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        isMobile={mobile}
                        sync={true}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                    />
                </div>
            </DashboardShell>
        </div>
    );
};

export default ListingSync;
